import { useState } from 'react';
import { Link, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { UserRole } from '@klaro7/shared-types';
import { Alert, AlertDescription } from '@klaro7/ui';

import { PasswordToggleButton } from '@/components/PasswordInput';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { getDefaultRedirect } from '@/features/auth/context/AuthContext';
import { useRegistrationStatus } from '@/features/settings/hooks/useServicesSettings';
import {
  needsPhoneVerification,
  PHONE_VERIFICATION_PATH,
} from '@/features/phone-verification/utils/phoneGate';
import {
  needsEmailVerification,
  EMAIL_VERIFICATION_PATH,
} from '@/features/email-verification/utils/emailGate';

export function LoginPage() {
  const { isAuthenticated, isLoading: authLoading, user, login } = useAuth();
  const { data: registrationStatus } = useRegistrationStatus();
  const registrationOpen = registrationStatus?.registrationEnabled ?? false;
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  if (authLoading) {
    return null;
  }

  if (isAuthenticated && user) {
    if (user.mustChangePassword) return <Navigate to="/change-password" replace />;
    if (needsEmailVerification(user)) return <Navigate to={EMAIL_VERIFICATION_PATH} replace />;
    if (needsPhoneVerification(user)) return <Navigate to={PHONE_VERIFICATION_PATH} replace />;
    return <Navigate to={getDefaultRedirect(user.role)} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const loggedUser = await login(email, password);
      if (loggedUser.mustChangePassword) {
        navigate('/change-password', { replace: true });
        return;
      }
      const savedFrom = location.state?.from?.pathname;
      const role = loggedUser?.role ?? UserRole.USER;
      const to = (savedFrom && role !== UserRole.USER) ? savedFrom : getDefaultRedirect(role);
      // Registrazione interrotta prima della conferma email o dell'OTP: si
      // riprende da lì. Email prima del telefono (doppio opt-in).
      if (needsEmailVerification(loggedUser)) {
        navigate(EMAIL_VERIFICATION_PATH, { replace: true, state: { redirectTo: to } });
        return;
      }
      if (needsPhoneVerification(loggedUser)) {
        navigate(PHONE_VERIFICATION_PATH, { replace: true, state: { redirectTo: to } });
        return;
      }
      navigate(to, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login fallito. Riprova.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="relative flex min-h-screen items-center justify-center px-4 py-8"
      style={{
        background: 'linear-gradient(160deg, #231F55 0%, #1a1740 40%, #126f7d 100%)',
        // Spazio per il banner cookie: su telefono coprirebbe il bottone Accedi.
        paddingBottom: 'calc(2rem + var(--cookie-banner-h, 0px))',
      }}
    >
      {/* Subtle radial glow */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at 30% 20%, rgba(24,153,170,0.15) 0%, transparent 60%), radial-gradient(ellipse at 70% 80%, rgba(118,185,185,0.08) 0%, transparent 50%)',
        }}
      />

      <div className="relative z-10 w-full max-w-md">
        {/* Back to home */}
        <div className="mb-6">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-white/50 transition-colors hover:text-white/80"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
              <path fillRule="evenodd" d="M17 10a.75.75 0 0 1-.75.75H5.612l4.158 3.96a.75.75 0 1 1-1.04 1.08l-5.5-5.25a.75.75 0 0 1 0-1.08l5.5-5.25a.75.75 0 1 1 1.04 1.08L5.612 9.25H16.25A.75.75 0 0 1 17 10Z" clipRule="evenodd" />
            </svg>
            Torna alla piattaforma
          </Link>
        </div>

        {/* Logo */}
        <div className="mb-8 flex justify-center">
          <img src="/images/logo-clean.png" alt="Klaro7" className="h-14 drop-shadow-lg" />
        </div>

        {/* Card */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-8 shadow-2xl backdrop-blur-xl sm:p-10">
          <div className="mb-6 text-center">
            <h1 className="text-2xl font-semibold tracking-tight text-white">
              Bentornato
            </h1>
            <p className="mt-1.5 text-sm text-white/60">
              Inserisci le tue credenziali per accedere
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <Alert variant="error">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Email */}
            <div className="space-y-1.5">
              <label htmlFor="login-email" className="block text-sm font-medium text-white/70">
                Email
              </label>
              <input
                id="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nome@email.com"
                required
                autoComplete="email"
                className="flex h-11 w-full rounded-lg border border-white/15 bg-white/[0.06] px-3.5 py-2 text-sm text-white placeholder:text-white/30 transition-colors duration-200 focus:border-brand-teal/50 focus:bg-white/[0.1] focus:outline-none focus:ring-2 focus:ring-brand-teal/20"
              />
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label htmlFor="login-password" className="block text-sm font-medium text-white/70">
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  className="text-xs font-medium text-brand-teal transition-colors hover:text-brand-cyan hover:underline"
                >
                  Password dimenticata?
                </Link>
              </div>
              <div className="relative">
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="La tua password"
                  required
                  autoComplete="current-password"
                  className="flex h-11 w-full rounded-lg border border-white/15 bg-white/[0.06] px-3.5 py-2 pr-10 text-sm text-white placeholder:text-white/30 transition-colors duration-200 focus:border-brand-teal/50 focus:bg-white/[0.1] focus:outline-none focus:ring-2 focus:ring-brand-teal/20"
                />
                <PasswordToggleButton
                  visible={showPassword}
                  onToggle={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-white/40 transition-colors hover:text-white/70"
                />
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="mt-2 flex h-11 w-full items-center justify-center gap-2 rounded-lg font-medium text-white shadow-md transition-all duration-300 hover:shadow-lg hover:shadow-brand-teal/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal focus-visible:ring-offset-2 focus-visible:ring-offset-[#231F55] active:scale-[0.98] disabled:pointer-events-none disabled:opacity-60"
              style={{
                background: 'linear-gradient(135deg, #1899AA 0%, #126f7d 100%)',
              }}
            >
              {isLoading ? (
                <>
                  <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span className="text-sm">Accesso in corso...</span>
                </>
              ) : (
                <span className="text-sm">Accedi</span>
              )}
            </button>
          </form>

          {/* Register link — only when registrations are open */}
          {registrationOpen && (
            <p className="mt-6 text-center text-sm text-white/40">
              Non hai un account?{' '}
              <Link to="/register" className="font-medium text-brand-teal transition-colors hover:text-brand-cyan hover:underline">
                Registrati
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
